import { useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { TOKEN_EXPIRED, UNAUTHENTICATED } from '@speeki/dictionary';
import { notify } from '@speeki/global-ui-components';
import { StorageManager } from '@speeki/storage-manager';

import {
    ConfirmMfaSetupDocument,
    RequestMfaSetupDocument,
} from '@graphql/generated/graphql';

import { AuthService } from '@services/AuthService/AuthService';
import { useLoginAfterSkipMfa } from '@services/AuthService/useLoginAfterSkipMfa';
import { getPath } from '@services/RouteService/paths';

import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';

export const MFA_QR_CODE_SIZE = 166;

export const useSetupMfaPage = () => {
    const [showManualCode, setShowManualCode] = useState(false);
    const [qrCode, setQrCode] = useState<string | null>(null);

    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        clearErrors,
        control,
        formState: { errors, isSubmitted, isSubmitting },
        handleSubmit,
        setError,
    } = useForm<MfaFormValues>({
        reValidateMode: 'onSubmit',
        resolver: yupResolver(MfaFormSchema),
    });

    const [requestMfaSetup, { data: requestSetupData }] = useMutation(
        RequestMfaSetupDocument,
    );
    const [confirmMfaSetup, { loading: confirmMfaLoading }] = useMutation(
        ConfirmMfaSetupDocument,
        {
            context: {
                notifyOnError: {
                    exceptions: [UNAUTHENTICATED, TOKEN_EXPIRED],
                },
            },
            onCompleted: () => {
                const metadata = AuthService.getMfaMetadata();

                if (metadata)
                    AuthService.setMfaMetadata({
                        ...metadata,
                        isAppAssigned: true,
                    });

                navigate(getPath({ path: '/auth/setup-mfa/recovery-email' }));
            },
            onError: (error) => {
                const isCodeInvalid =
                    error.graphQLErrors[0]?.extensions?.code ===
                    UNAUTHENTICATED;

                if (isCodeInvalid)
                    setError('code', {
                        message: 'INVALID_CODE',
                        type: 'invalid-code',
                    });
            },
        },
    );
    const { handleSkipMfa } = useLoginAfterSkipMfa();

    const secret = requestSetupData?.setup.secret;
    const metadata = AuthService.getMfaMetadata();

    const {
        field: { onChange, ...field },
    } = useController({ control, name: 'code' });

    const onSubmit = handleSubmit(({ code }) => {
        void confirmMfaSetup({
            variables: {
                input: code,
            },
        });
    });

    useEffect(() => {
        const mfaToken = StorageManager.getValue('graphSessionToken');

        if (mfaToken && !metadata?.isAppAssigned)
            void requestMfaSetup({
                onCompleted: ({ setup: { qrCode } }) => {
                    const qrCodeValue = qrCode.split('&chl=')[1];

                    if (!qrCodeValue) throw new Error('QR code is not valid');

                    setQrCode(decodeURIComponent(qrCodeValue));
                },
                onError: () => {
                    notify(t('SETUP_MFA_REQUEST_ERROR')).ERROR();
                    void AuthService.logout(true);
                },
                variables: {
                    input: {
                        method: 'OTP',
                        qrCodeSize: MFA_QR_CODE_SIZE,
                    },
                },
            });
    }, [requestMfaSetup]);

    return {
        clearErrors,
        confirmMfaLoading,
        errors,
        field,
        handleSkipMfa,
        isSubmitted,
        isSubmitting,
        metadata,
        onChange,
        onSubmit,
        qrCode,
        secret,
        setShowManualCode,
        showManualCode,
    };
};

const MfaFormSchema = yup.object({
    code: yup.string().required('TOO_SHORT_CODE').min(6, 'TOO_SHORT_CODE'),
});

type MfaFormValues = yup.InferType<typeof MfaFormSchema>;
